import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, inject, provideAppInitializer } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';

import { firstValueFrom, timeout } from 'rxjs';

import { AuthInterceptor } from '@core/auth/auth.interceptor';
import { UserService } from '@core/auth/user.service';
import { LookupService } from '@core/services/lookup.service';
import { SharedApiService } from '@core/services/shared-api.service';

import { APP_ROUTES } from './app.routes';
import { LegacyStorageBridge } from './legacy-storage-bridge';

const LOOKUP_WAIT_MS = 5000;

/**
 * Refreshes the stored lookup list for a signed-in staff user. Start-up waits for it only when nothing is stored yet,
 * and never for long, so screens that read the list when they open find it; a failed or slow fetch keeps whatever is
 * stored and never blocks start-up. An external user's token is left alone: the shell turns that user away, and an
 * API call made with it here could end their session in their own portal.
 */
function loadLookups(): Promise<void> {
  const userService = inject(UserService);
  const sharedApi = inject(SharedApiService);
  const lookupService = inject(LookupService);

  const token = userService.getAuthToken();
  if (!token || !userService.isInternalUser(token)) return Promise.resolve();

  const fetch = firstValueFrom(sharedApi.getLookups().pipe(timeout(LOOKUP_WAIT_MS)))
    .then(lookups => lookupService.set(JSON.stringify(lookups)))
    .catch(error => console.error('Error fetching lookups:', error));

  return lookupService.get() ? Promise.resolve() : fetch;
}

export const appConfig: ApplicationConfig = {
  providers: [
    // Before routing starts, so guards and screens read the values the previous release stored.
    provideAppInitializer(() => inject(LegacyStorageBridge).run()),
    provideAppInitializer(loadLookups),
    provideRouter(APP_ROUTES),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    provideAnimationsAsync()
  ]
};
